import type { AnalyticsEvent, AnalyticsSetup } from "./analytics";

/**
 * What the app asks of a tracker, whichever one the project configured.
 *
 * An interface rather than a union the callers switch on: legacy branched on the
 * type at all three call sites, so every new tracker meant touching all of them,
 * and "this one can't do that" was expressed as a `console.info` in the middle of
 * the caller.
 */
export interface Tracker {
  pageView(page: string): void;
  event(event: AnalyticsEvent): void;
  /** Extra attributes attached to subsequent hits. Not every tracker has them. */
  setDimensions(dimensions: Record<string, string>): void;
}

/**
 * Build the configured tracker, loading only its SDK.
 *
 * Each is imported on demand: a project uses at most one, most use none, and
 * between them the three SDKs are about half a megabyte of dependency that would
 * otherwise sit in every tenant's bundle.
 */
export async function createTracker(setup: AnalyticsSetup): Promise<Tracker> {
  switch (setup.type) {
    case "google":
      return createGoogle(setup);
    case "matomo":
      return createMatomo(setup);
    case "gtm":
      return createGtm(setup);
  }
}

async function createGoogle(setup: AnalyticsSetup): Promise<Tracker> {
  const { default: ReactGA } = await import("react-ga4");
  ReactGA.initialize(setup.id);

  // No `ga('set', 'anonymizeIp', true)` as legacy had: that is a Universal
  // Analytics call, which GA4 does not implement — it anonymises IPs itself and
  // offers no way to turn that off.
  return {
    pageView: (page) => ReactGA.send({ hitType: "pageview", page }),
    event: ({ category, action, label }) =>
      ReactGA.event({ category, action, label }),
    setDimensions: (dimensions) => ReactGA.set(dimensions),
  };
}

async function createMatomo(setup: AnalyticsSetup): Promise<Tracker> {
  const { default: MatomoTracker } = await import("@datapunt/matomo-tracker-js");
  const matomo = new MatomoTracker({
    urlBase: setup.matomoBaseUrl as string,
    siteId: Number(setup.id),
  });

  return {
    pageView: (page) => matomo.trackPageView({ href: page }),
    event: ({ category, action, label }) =>
      matomo.trackEvent({ category, action, name: label }),
    // Matomo has custom dimensions, but they are per-hit rather than sticky, so
    // there is nothing to set ahead of time.
    setDimensions: () => {},
  };
}

async function createGtm(setup: AnalyticsSetup): Promise<Tracker> {
  const { default: TagManager } = await import("react-gtm-module");
  TagManager.initialize({ gtmId: setup.id });

  return {
    // Tag Manager is a container, not a tracker: whatever it forwards to counts
    // page views itself, so pushing one here would double them.
    pageView: () => {},
    event: ({ category, action, label }) =>
      TagManager.dataLayer({ dataLayer: { event: action, category, label } }),
    setDimensions: () => {},
  };
}
